import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import Select from "./Select.jsx";
import { STIPPLE_DOT_RADIUS, STIPPLE_SIZE } from "../lib/stipple.js";
import { SEVERITY_COLOR } from "../lib/traceReview.js";
import { parseServerTimestamp } from "../lib/traceUtils.js";
import {
    DAY_PRESETS, HOUR_PRESETS, bucketOf, monotonePath, poolSpan, presetFits, presetOf, presetWindow, spanLabel,
    windowBuckets, windowCounts, windowFractions, windowFromFractions,
} from "../lib/triageWindow.js";

// The window the Triage tab reads within its loaded pool: a menu that names
// what the pool covers (and loads older findings while it is clipped), the
// last hour, six hours or day at a click, and the pool's findings over time
// as a smooth line over a stipple fill, the window framed on it with a grip
// on each edge, as the trace workspace frames its window strip. The body
// slides the window, a grip moves its end, a drag beside it draws a new one
// and a click beside it moves it there. A drag commits on release;
// double-click goes back to the whole pool. The count beside it is the
// window's. The line is drawn at the track's own pixel width so the stipple
// stays a grid of round dots.
const FRAME_PAD = 3;
const BAND_HEIGHT = 34;
const TRACK_HEIGHT = BAND_HEIGHT + 2 * FRAME_PAD;
const BASELINE = FRAME_PAD + BAND_HEIGHT - 1.5;
const CURVE_TOP = FRAME_PAD + 4;
const PIN_Y = FRAME_PAD + 4;
// The activity chart's stipple: the shared grid and dot (lib/stipple.js), the
// dot where its renderer puts it, at its fill opacity inside the window.
const STIPPLE_DOT_AT = 2;
const STIPPLE_OPACITY = 0.55;
const STIPPLE_OPACITY_OUTSIDE = 0.2;
const BUCKET_PX = 6;
// The workspace strip's grip. The time scale starts and ends half a grip in
// from the track's sides, so a grip at either end of the pool stays inside the
// track instead of hanging past the pane's edge.
const GRIP_W = 8;
const EDGE = GRIP_W / 2;
// How near a grip takes its end, the body a narrow window keeps between its
// grips (they part around it) so it can still be slid, and how far the
// pointer goes before a press is a drag.
const GRAB_PX = 5;
const BODY_MIN_PX = 8;
const DRAG_PX = 3;
const KEY_STEP = 1 / 96;
const FMT = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const rangeText = (w) => FMT.formatRange(new Date(w.fromMs), new Date(w.toMs));
const PRESET_LABELS = { "1h": "Last hour", "6h": "Last 6 hours", "24h": "Last 24 hours", "3d": "Last 3 days", "7d": "Last 7 days" };
const CONTROL = "min-h-[30px] border border-[color:var(--line)] px-[9px] text-[11px] leading-[1.08] tracking-[0.04em] transition-colors";
const clamp01 = (v) => Math.min(1, Math.max(0, v));

const MET = SEVERITY_COLOR.danger;
const PAD_PX = 14;
const MIN_PAD_MS = 60 * 1000;
const TIME = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
const atOf = (r) => parseServerTimestamp(r?.completed_at);

// Where the grips sit over a window framed from xa to xb on a track `w` wide:
// on its edges, or parted around a window too narrow to keep a body between
// them.
function gripsAt(xa, xb, w) {
    const mid = (xa + xb) / 2;
    const part = BODY_MIN_PX / 2 + GRIP_W / 2;
    return [Math.max(EDGE, Math.min(xa, mid - part)), Math.min(w - EDGE, Math.max(xb, mid + part))];
}
// What is under the pointer at x: a grip ("from", "to"), the window's body,
// or the track beside it.
function partAt(x, xa, xb, w) {
    const [gl, gr] = gripsAt(xa, xb, w);
    const dl = Math.abs(x - gl);
    const dr = Math.abs(x - gr);
    if (Math.min(dl, dr) <= GRAB_PX) return dl <= dr ? "from" : "to";
    return x > gl && x < gr ? "body" : "track";
}
// What a drag from there does: move an end, slide the window, or draw a new
// one (the whole pool has nowhere to slide, so its body draws).
const dragKind = (part, window) => (part === "from" || part === "to" ? part : part === "body" && window ? "slide" : "draw");

export default function TriageTimeWindow({ rows = [], window = null, onChange, canLoadMore = false, loadingMore = false, onLoadMore, marked = null, metByOf = null, onHoverMet = null, onPickMet = null }) {
    const pool = useMemo(() => poolSpan(rows), [rows]);
    const [width, setWidth] = useState(480);
    const scaleW = Math.max(1, width - 2 * EDGE);
    // The track runs a little past the oldest and newest findings, so the
    // grips at either end of the pool sit beside its first and last pins, not
    // on them.
    const span = useMemo(() => {
        if (pool.fromMs == null) return pool;
        const pad = Math.max(MIN_PAD_MS, (pool.toMs - pool.fromMs) * (PAD_PX / Math.max(1, scaleW - 2 * PAD_PX)));
        return { ...pool, fromMs: pool.fromMs - pad, toMs: pool.toMs + pad };
    }, [pool, scaleW]);
    const bucketCount = Math.max(24, Math.min(160, Math.round(scaleW / BUCKET_PX)));
    const buckets = useMemo(() => windowBuckets(rows, span, bucketCount), [rows, span, bucketCount]);
    // A press on the track: what it does (dragKind) and the part it took, the
    // window it began with, where it began (client px and place on the scale)
    // and, once the pointer has moved, where the pointer is now.
    const [drag, setDrag] = useState(null);
    // The part under the pointer while nothing is held.
    const [over, setOver] = useState(null);
    // Whether every row the hovered pin lights in the list is out of view.
    const [litOff, setLitOff] = useState(false);
    // The met pin under the pointer, by its bucket.
    const [hot, setHot] = useState(null);
    const trackRef = useRef(null);
    const uid = `triage-window-${useId().replace(/:/g, "")}`;
    const hasSpan = span.fromMs != null;
    useLayoutEffect(() => {
        const el = trackRef.current;
        if (!el) return undefined;
        const measure = (w) => { if (w > 0) setWidth(Math.round(w)); };
        measure(el.getBoundingClientRect().width);
        if (typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver((entries) => measure(entries[entries.length - 1].contentRect.width));
        observer.observe(el);
        return () => observer.disconnect();
    }, [hasSpan]);
    // A window reaches no further than the pool, and one that takes in all of
    // it is the whole pool.
    const toWindow = (lo, hi) => {
        const w = windowFromFractions(span, lo, hi);
        if (!w || (w.fromMs <= pool.fromMs && w.toMs >= pool.toMs)) return null;
        return { fromMs: Math.max(w.fromMs, pool.fromMs), toMs: Math.min(w.toMs, pool.toMs) };
    };
    // Where a window is drawn along the track. An end at the oldest or newest
    // finding is drawn at that end of the track, as the whole pool's are, so
    // its grip sits beside the pin there and a grip dragged past the pin goes
    // on to the end.
    const drawnAt = (w) => {
        const [a, b] = windowFractions(span, w);
        return [w && w.fromMs > pool.fromMs ? a : 0, w && w.toMs < pool.toMs ? b : 1];
    };
    // The window a drag makes: one end moved, up to the other; the window slid
    // along the pool at its own length; or a new one drawn from where the
    // drag began.
    const dragged = (d) => {
        const [a0, b0] = windowFractions(span, d.window);
        if (d.kind === "from") return toWindow(Math.min(d.at, b0), b0);
        if (d.kind === "to") return toWindow(a0, Math.max(d.at, a0));
        if (d.kind === "slide") {
            const length = d.window.toMs - d.window.fromMs;
            const fromMs = Math.round(Math.min(pool.toMs - length, Math.max(pool.fromMs, d.window.fromMs + (d.at - d.f) * (span.toMs - span.fromMs))));
            return { fromMs, toMs: fromMs + length };
        }
        return toWindow(d.f, d.at);
    };
    const shown = drag?.moved ? dragged(drag) : window;
    const counts = useMemo(() => windowCounts(rows, shown), [rows, shown]);
    const metRows = useMemo(() => (marked?.size ? rows.filter((r) => marked.has(String(r?.finding_id))) : []), [rows, marked]);
    const metBuckets = useMemo(() => windowBuckets(metRows, span, bucketCount), [metRows, span, bucketCount]);
    // Each bucket's met findings, newest first, for the hover on its column.
    const metAt = useMemo(() => {
        const at = new Map();
        for (const r of metRows) {
            const i = bucketOf(r, span, bucketCount);
            if (i == null) continue;
            at.set(i, [...(at.get(i) || []), r]);
        }
        for (const list of at.values()) list.sort((x, y) => (atOf(y) || 0) - (atOf(x) || 0));
        return at;
    }, [metRows, span, bucketCount]);
    const hotRows = hot != null ? metAt.get(hot) || null : null;
    const hotKey = hotRows ? hotRows.map((r) => r.finding_id).join(",") : "";
    // The host lights the hovered pin's rows; the ids change only with the pin.
    useEffect(() => { onHoverMet?.(hotKey ? new Set(hotKey.split(",")) : null); }, [hotKey]); // eslint-disable-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
        if (!hotKey) { setLitOff(false); return undefined; }
        // The rows light on the list's next render, so look on the frame after.
        const id = requestAnimationFrame(() => {
            const lit = [...(trackRef.current?.closest("[data-lite-list]")?.querySelectorAll("[data-lite-lit]") || [])];
            const vh = globalThis.innerHeight || 0;
            setLitOff(lit.length > 0 && lit.every((el) => { const r = el.getBoundingClientRect(); return r.top > vh - 24 || r.bottom < 0; }));
        });
        return () => cancelAnimationFrame(id);
    }, [hotKey]);
    const metShown = useMemo(() => windowCounts(metRows, shown).findings, [metRows, shown]);
    if (span.fromMs == null) return null;
    // The menu, the hour buttons and the count follow a drag as it goes.
    const preset = presetOf(pool, shown);
    // Widest first, down from the whole pool.
    const presets = [...DAY_PRESETS, ...HOUR_PRESETS].filter(([, length]) => presetFits(pool, length)).sort((x, y) => y[1] - x[1]);
    const options = [
        { value: "all", label: <span>All time ({spanLabel(pool.toMs - pool.fromMs)}) <span className="text-[color:var(--muted-soft)]">{pool.findings.toLocaleString()}</span></span> },
        ...presets.map(([id]) => ({ value: id, label: PRESET_LABELS[id] })),
        ...(shown && !preset ? [{ value: "custom", label: rangeText(shown), disabled: true }] : []),
        ...(canLoadMore ? [{ value: "more", label: loadingMore ? "Loading older findings..." : "Load older findings", disabled: loadingMore }] : []),
    ];
    const choose = (value) => {
        if (value === "more") { onLoadMore?.(); return; }
        if (value === "all") { onChange?.(null); return; }
        const length = [...DAY_PRESETS, ...HOUR_PRESETS].find(([id]) => id === value)?.[1];
        if (length) onChange?.(presetWindow(pool, length));
    };

    // The track as the pointer meets it: its box, and the scale across it.
    const scaleAt = () => {
        const rect = trackRef.current?.getBoundingClientRect();
        if (!rect?.width) return null;
        const w = Math.max(1, rect.width - 2 * EDGE);
        return { rect, x: (f) => EDGE + f * w, f: (clientX) => clamp01((clientX - rect.left - EDGE) / w) };
    };
    const partUnder = (clientX) => {
        const g = scaleAt();
        if (!g) return null;
        const [a, b] = drawnAt(window);
        return partAt(clientX - g.rect.left, g.x(a), g.x(b), g.rect.width);
    };
    const onPointerDown = (event) => {
        if (event.button !== 0) return;
        const g = scaleAt();
        if (!g) return;
        const part = partUnder(event.clientX);
        const f = g.f(event.clientX);
        setDrag({ kind: dragKind(part, window), part, window, clientX: event.clientX, f, at: f, moved: false });
        event.currentTarget.setPointerCapture?.(event.pointerId);
    };
    // The met column under the pointer, a few pixels either side.
    const metUnder = (clientX) => {
        const rect = trackRef.current?.getBoundingClientRect();
        if (!rect || !metAt.size) return null;
        const x = clientX - rect.left;
        let best = null;
        for (const i of metAt.keys()) {
            const d = Math.abs(xAt(i) - x);
            if (d <= Math.max(6, step / 2 + 2) && (best == null || d < best.d)) best = { i, d };
        }
        return best?.i ?? null;
    };
    const onPointerMove = (event) => {
        if (drag) {
            if (!drag.moved && Math.abs(event.clientX - drag.clientX) < DRAG_PX) return;
            const g = scaleAt();
            if (g) setDrag({ ...drag, moved: true, at: g.f(event.clientX) });
            return;
        }
        setOver(partUnder(event.clientX));
        setHot(metUnder(event.clientX));
    };
    const onPointerUp = (event) => {
        const d = drag;
        if (!d) return;
        setDrag(null);
        if (d.moved) {
            const next = dragged(d);
            if (next?.fromMs !== d.window?.fromMs || next?.toMs !== d.window?.toMs) onChange?.(next);
            return;
        }
        // A click on a met pin opens its findings in the list; one beside the
        // window moves the window there, at its own length.
        const i = metUnder(event.clientX);
        if (i != null) {
            onPickMet?.((metAt.get(i) || []).map((r) => String(r.finding_id)));
            return;
        }
        if (d.part === "track" && d.window) {
            const length = d.window.toMs - d.window.fromMs;
            const at = span.fromMs + d.f * (span.toMs - span.fromMs);
            const fromMs = Math.round(Math.min(pool.toMs - length, Math.max(pool.fromMs, at - length / 2)));
            onChange?.({ fromMs, toMs: fromMs + length });
        }
    };
    const nudge = (end, event) => {
        const step = (event.shiftKey ? 10 : 1) * KEY_STEP;
        const delta = event.key === "ArrowLeft" || event.key === "ArrowDown" ? -step : event.key === "ArrowRight" || event.key === "ArrowUp" ? step : 0;
        if (!delta) return;
        event.preventDefault();
        // Steps are shares of the pool, so the first one leaves the padding.
        // An end stops at the other end, as it does dragged.
        const [pa, pb] = windowFractions(pool, window);
        onChange?.(end === "from" ? windowFromFractions(pool, Math.min(clamp01(pa + delta), pb), pb) : windowFromFractions(pool, pa, Math.max(clamp01(pb + delta), pa)));
    };

    const max = Math.max(1, ...buckets);
    // Each bucket at its middle, so a pin sits where its findings fell.
    const step = scaleW / Math.max(1, buckets.length);
    const xAt = (i) => EDGE + (i + 0.5) * step;
    const [fa, fb] = drawnAt(shown);
    const xa = EDGE + fa * scaleW;
    const xb = EDGE + fb * scaleW;
    const [gripL, gripR] = gripsAt(xa, xb, width);
    // What the pointer has in hand: what it holds, else what it is over. The
    // frame darkens while the pointer is on the window; a grip lights while
    // it is the one in hand.
    const inHand = drag ? drag.kind : over ? dragKind(over, window) : null;
    const lit = inHand === "from" || inHand === "to" ? inHand : null;
    const frameHot = !!drag || (over != null && over !== "track");
    const cursor = lit ? "ew-resize" : inHand === "slide" ? (drag ? "grabbing" : "grab") : "crosshair";
    const curve = monotonePath(buckets.map((n, i) => [xAt(i), BASELINE - (n / max) * (BASELINE - CURVE_TOP)]));
    const area = curve ? `${curve}L${xAt(buckets.length - 1)},${BASELINE}L${xAt(0)},${BASELINE}Z` : "";
    const metCurve = metRows.length ? monotonePath(metBuckets.map((n, i) => [xAt(i), BASELINE - (n / max) * (BASELINE - CURVE_TOP)])) : "";
    // The findings a standard met, where they landed: a column the width of a
    // bucket with a tick on the baseline, a pin above it (numbered when it holds
    // more than one), and their share of the curve filled beneath it. Hovering a
    // column names its findings.
    const metMarks = !metRows.length ? null : (
        <g data-triage-window-met="">
            {metBuckets.map((n, i) => {
                if (!n) return null;
                const x = xAt(i);
                const w = Math.max(3, step);
                return (
                    <g key={i} data-triage-window-met-at={i} opacity={hot != null && hot !== i ? 0.55 : 1}>
                        <rect x={x - w / 2} y={FRAME_PAD} width={w} height={BASELINE - FRAME_PAD} fill={MET} opacity="0.16" />
                        <rect x={x - w / 2} y={BASELINE - 3} width={w} height={3} fill={MET} />
                        <line x1={x} y1={PIN_Y} x2={x} y2={BASELINE} stroke={MET} strokeWidth="1" strokeDasharray="2 2" />
                        <circle cx={x} cy={PIN_Y} r={(n > 1 ? 5 : 3.5) + (hot === i ? 1 : 0)} fill={MET} />
                        {n > 1 ? <text x={x} y={PIN_Y + 2.5} textAnchor="middle" fontSize="8" fill="var(--bg)">{n}</text> : null}
                    </g>
                );
            })}
            <path d={`${metCurve}L${xAt(metBuckets.length - 1)},${BASELINE}L${xAt(0)},${BASELINE}Z`} fill={MET} opacity="0.75" />
        </g>
    );
    // The workspace strip's grip on each edge of the frame: a pill with two
    // ridges, green while it is in hand or focused.
    const grip = (end, x) => {
        const time = end === "from" ? (shown?.fromMs ?? pool.fromMs) : (shown?.toMs ?? pool.toMs);
        const ridge = `h-[8px] w-[1px] group-focus-visible:bg-[color:var(--green)] ${lit === end ? "bg-[color:var(--green)]" : "bg-[color:var(--muted)]"}`;
        return (
            <span
                role="slider"
                tabIndex={0}
                data-triage-window-handle={end}
                aria-label={end === "from" ? "Window start" : "Window end"}
                aria-valuemin={pool.fromMs}
                aria-valuemax={pool.toMs}
                aria-valuenow={time}
                aria-valuetext={FMT.format(new Date(time))}
                onKeyDown={(event) => nudge(end, event)}
                className={`group absolute top-1/2 flex h-[18px] w-[8px] -translate-y-1/2 items-center justify-center gap-[1px] rounded-[2px] border bg-[color:var(--panel)] outline-none focus-visible:border-[color:var(--green)] ${lit === end ? "border-[color:var(--green)]" : "border-[color:var(--line-strong)]"}`}
                style={{ left: Math.round(x) - GRIP_W / 2 }}
            >
                <span aria-hidden="true" className={ridge} />
                <span aria-hidden="true" className={ridge} />
            </span>
        );
    };
    const dots = (id, opacity) => (
        <pattern id={id} width={STIPPLE_SIZE} height={STIPPLE_SIZE} patternUnits="userSpaceOnUse">
            <circle cx={STIPPLE_DOT_AT} cy={STIPPLE_DOT_AT} r={STIPPLE_DOT_RADIUS} opacity={opacity} style={{ fill: "var(--ink)" }} />
        </pattern>
    );

    return (
        <div data-triage-window="" role="group" aria-label="Time window" className="flex flex-wrap items-center gap-x-[10px] gap-y-[8px]">
            {/* The field variant fills its parent; this one sizes to its label. */}
            <span className="shrink-0">
                <Select value={preset || (shown ? "custom" : "all")} onChange={(v) => choose(String(v))} options={options} ariaLabel="Findings to triage" />
            </span>
            <span className="flex">
                {HOUR_PRESETS.map(([id, length], i) => (
                    <button
                        key={id}
                        type="button"
                        data-triage-window-preset={id}
                        aria-pressed={preset === id}
                        disabled={!presetFits(pool, length)}
                        onClick={() => onChange?.(preset === id ? null : presetWindow(pool, length))}
                        className={`${CONTROL} ${i ? "-ml-px" : ""} ${preset === id ? "relative border-[color:var(--line-strong)] bg-[color:var(--control-bg)] text-[color:var(--ink)]" : "bg-transparent text-[color:var(--muted)] hover:text-[color:var(--ink)]"} cursor-pointer disabled:cursor-default disabled:opacity-40`}
                    >
                        {id}
                    </button>
                ))}
            </span>
            <div
                ref={trackRef}
                data-triage-window-track=""
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={() => setDrag(null)}
                onPointerLeave={() => { setHot(null); setOver(null); }}
                onDoubleClick={() => onChange?.(null)}
                className="relative order-last min-w-[160px] basis-full touch-none select-none"
                style={{ height: TRACK_HEIGHT, cursor }}
            >
                <svg width={width} height={TRACK_HEIGHT} viewBox={`0 0 ${width} ${TRACK_HEIGHT}`} aria-hidden="true" className="absolute inset-0 overflow-visible">
                    <defs>
                        {dots(`${uid}-dots`, STIPPLE_OPACITY)}
                        {dots(`${uid}-dots-out`, STIPPLE_OPACITY_OUTSIDE)}
                        <clipPath id={`${uid}-kept`}><rect x={xa} y="-2" width={Math.max(0, xb - xa)} height={TRACK_HEIGHT + 4} /></clipPath>
                    </defs>
                    <line x1={EDGE} y1={BASELINE} x2={width - EDGE} y2={BASELINE} stroke="var(--line)" strokeWidth="1.5" />
                    <path d={area} fill={`url(#${uid}-dots-out)`} />
                    <path d={area} fill={`url(#${uid}-dots)`} clipPath={`url(#${uid}-kept)`} />
                    <path d={curve} fill="none" stroke="var(--muted-soft)" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
                    <path d={curve} fill="none" stroke="var(--ink)" strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" clipPath={`url(#${uid}-kept)`} />
                    {metMarks}
                    <rect
                        data-triage-window-frame=""
                        x={Math.round(xa) + 0.5}
                        y={0.5}
                        width={Math.max(2, Math.round(xb - xa) - 1)}
                        height={TRACK_HEIGHT - 1}
                        fill="none"
                        stroke="var(--ink)"
                        strokeOpacity={frameHot ? 1 : 0.65}
                    />
                </svg>
                {grip("from", gripL)}
                {grip("to", gripR)}
                {/* A line per finding at the hovered pin: when, what, and the
                    standard that met it. The list lights their rows. It opens
                    from its pin, and the room before it gives way first, so it
                    never runs past the track's sides. */}
                {hotRows && !drag ? (
                    <span className="pointer-events-none absolute inset-x-0 top-[calc(100%+4px)] z-30 flex">
                        <span aria-hidden="true" style={{ width: Math.max(0, xAt(hot) - 10) }} />
                        <span
                            data-triage-window-met-hover=""
                            className="grid max-w-[min(520px,100%)] shrink-0 grid-cols-[minmax(0,1fr)] gap-[2px] rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] px-[8px] py-[4px] text-[color:var(--ink)] shadow-[0_2px_8px_rgba(0,0,0,0.1)]"
                        >
                            {hotRows.map((h) => {
                                const t = atOf(h);
                                return (
                                    <span key={h.finding_id} data-triage-window-met-line="" className="flex items-center gap-x-[6px] text-[11px] leading-[15px] whitespace-nowrap">
                                        <span aria-hidden="true" className="h-[6px] w-[6px] shrink-0 rounded-full" style={{ background: MET }} />
                                        <span className="w-[52px] shrink-0 tabular-nums text-[color:var(--muted)]">{Number.isFinite(t) ? TIME.format(new Date(t)) : ""}</span>
                                        <span className="min-w-0 truncate">{h.title}</span>
                                        <span className="shrink-0 text-[10px] text-[color:var(--muted)]">{(metByOf?.(h) || []).join(", ")}</span>
                                    </span>
                                );
                            })}
                            {litOff ? <span data-triage-window-met-below="" className="text-[10px] leading-[13px]" style={{ color: MET }}>{"\u2193 below"}</span> : null}
                        </span>
                    </span>
                ) : null}
            </div>
            <span data-triage-window-count="" className="text-[11px] leading-[1.18] text-[color:var(--muted)]">
                {counts.findings.toLocaleString()} {"\u00b7"} {counts.sessions.toLocaleString()} trace{counts.sessions === 1 ? "" : "s"}
                {marked ? <> {"\u00b7"} <span data-triage-window-met-count="" style={{ color: metShown ? MET : undefined }}>{metShown.toLocaleString()} met</span></> : null}
            </span>
        </div>
    );
}
